<template>
  <v-app>
    <v-app-bar
      :elevation="0"
      class="px-0 app-bar-transition"
      :height="$vuetify.display.smAndDown ? '64' : '80'"
      :class="{
        'app-bar-hidden': shouldHideAppBar && $vuetify.display.smAndDown,
        'app-bar-visible': !shouldHideAppBar || !$vuetify.display.smAndDown
      }"
      fixed
    >
      <v-container fluid class="px-6">
        <div class="d-flex align-center">
          <router-link :to="brandTarget" class="text-decoration-none">
            <div class="d-flex align-center">
              <v-icon
                color="primary"
                size="32"
                class="mr-2"
              >
                mdi-floor-plan
              </v-icon>
              <span class="text-h6 font-weight-bold">{{ $t('nav.brand') }}</span>
            </div>
          </router-link>
          
          <v-spacer></v-spacer>
          
          <div class="d-none d-md-flex align-center gap-2">
            <!-- Marketing nav (Features / Preise / FAQ / Ratgeber)
                 + Sign-In/Sign-Up are public-flow chrome. Once the
                 user is authenticated, none of it has a place in
                 their workflow — the rail is their full menu. We hide
                 the lot and let the brand-link doubling as the
                 "home" target redirect them into /meine-analysen (see
                 router beforeEach + Home.vue watcher). -->
            <template v-if="!authStore.isAuthenticated">
              <v-btn
                v-for="(item, i) in menuItems"
                :key="i"
                variant="text"
                class="text-none font-weight-medium"
                @click="item.onClick ? item.onClick() : goToSection(item.section, item.to)"
              >
                {{ $t(item.title) }}
              </v-btn>

              <LanguageSwitcher class="mx-2" @language-changed="handleLanguageChange" />

              <v-divider vertical class="mx-2"></v-divider>

              <v-btn
                variant="text"
                to="/sign-in"
                class="text-none"
                elevation="0"
              >
                <v-icon icon="mdi-login" class="mr-1" size="small"></v-icon>
                {{ $t('nav.auth.signIn') }}
              </v-btn>
              <v-btn
                variant="elevated"
                to="/sign-up"
                color="primary"
                class="text-none"
                elevation="0"
              >
                <v-icon icon="mdi-account-plus" class="mr-1" size="small"></v-icon>
                {{ $t('nav.auth.signUp') }}
              </v-btn>
            </template>
            <template v-else>
              <!-- Authenticated desktop chrome reduces to just the
                   language switcher. Account actions (email, credits,
                   Meine Analysen, Abmelden) live on the persistent
                   AppAccountRail. Brand link clicks → / → router
                   redirects → /meine-analysen. -->
              <LanguageSwitcher class="mx-2" @language-changed="handleLanguageChange" />
            </template>

            <!--
              The desktop header keeps "Anmelden" + the filled "Registrieren"
              button as the conversion path. A second "Jetzt starten →" link
              next to "Registrieren" used to read as a duplicate CTA — we
              still expose it inside the mobile drawer where the auth links
              are list items rather than buttons.
            -->
          </div>
          
          <v-app-bar-nav-icon
            class="d-flex d-md-none"
            @click="drawer = !drawer"
          ></v-app-bar-nav-icon>
        </div>
      </v-container>
    </v-app-bar>

    <v-navigation-drawer
      v-if="isMobileNav"
      v-model="drawer"
      temporary
      location="right"
      width="320"
      class="mobile-nav-drawer"
    >
      <div class="mobile-nav-drawer__header">
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          :aria-label="$t('common.close')"
          @click="drawer = false"
        />
      </div>
      <v-list>
        <template v-if="isLandingPage">
          <v-list-item
            v-for="(item, i) in menuItems"
            :key="`landing-${i}`"
            @click="item.onClick ? item.onClick() : goToSection(item.section, item.to)"
            rounded="lg"
          >
            <template v-slot:prepend>
              <v-icon :icon="item.icon"></v-icon>
            </template>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item>
        </template>
        <template v-else>
          <v-list-item
            v-for="(item, i) in mobileAppMenuItems"
            :key="`app-${i}`"
            @click="item.onClick ? item.onClick() : goToSection(item.section, item.to)"
            rounded="lg"
          >
            <template v-slot:prepend>
              <v-icon :icon="item.icon"></v-icon>
            </template>
            <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
          </v-list-item>
        </template>

        <v-divider class="my-4"></v-divider>

        <template v-if="!authStore.isAuthenticated">
          <v-list-item
            to="/sign-in"
            rounded="lg"
            @click="drawer = false"
          >
            <template v-slot:prepend>
              <v-icon icon="mdi-login"></v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.auth.signIn') }}</v-list-item-title>
          </v-list-item>
          <v-list-item
            to="/sign-up"
            rounded="lg"
            @click="drawer = false"
          >
            <template v-slot:prepend>
              <v-icon icon="mdi-account-plus"></v-icon>
            </template>
            <v-list-item-title>{{ $t('nav.auth.signUp') }}</v-list-item-title>
          </v-list-item>
        </template>
        
        <template v-else>
          <v-list-item>
            <template v-slot:prepend>
              <v-icon icon="mdi-account-circle" class="mr-2"></v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ authStore.user?.email }}
            </v-list-item-title>
          </v-list-item>

          <!-- Single combined credits row: balance on top, explicit
               "Credits kaufen" call-to-action underneath. Tapping
               anywhere on the row opens the pricing section so the
               user can pick a package. -->
          <v-list-item
            style="cursor: pointer"
            data-testid="mobile-menu-credits"
            @click="handleCreditsClick"
          >
            <template v-slot:prepend>
              <v-icon icon="mdi-credit-card-plus" class="mr-2"></v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ $t('nav.menu.creditsBalance', { count: authStore.credits }) }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ $t('analysis.buyCredits') }}
            </v-list-item-subtitle>
          </v-list-item>

          <v-list-item
            v-if="isLandingPage"
            :to="{ name: 'AnalysesDashboard' }"
            rounded="lg"
            data-testid="mobile-landing-analyses"
            @click="handleMobileAnalysisClick"
          >
            <template v-slot:prepend>
              <v-icon icon="mdi-folder-multiple-image" class="mr-2"></v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ $t('nav.menu.analysis') }}
            </v-list-item-title>
          </v-list-item>

          <v-list-item
            @click="handleSignOut"
            color="error"
            rounded="lg"
          >
            <template v-slot:prepend>
              <v-icon icon="mdi-logout" class="mr-2"></v-icon>
            </template>
            <v-list-item-title class="text-body-2">
              {{ $t('nav.auth.signOut') }}
            </v-list-item-title>
          </v-list-item>
        </template>

        <v-list-item>
          <LanguageSwitcher @language-changed="handleLanguageChange" />
        </v-list-item>

        <v-list-item v-if="showGetStartedButton" class="mt-4">
          <v-btn
            block
            color="primary"
            class="text-none"
            elevation="0"
            @click="scrollToTop"
          >
            {{ $t('nav.getStarted') }}
            <v-icon right class="ml-2">mdi-arrow-right</v-icon>
          </v-btn>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <v-main class="pt-16">
      <router-view></router-view>
    </v-main>

    <v-footer class="bg-background">
      <v-container>
        <v-row align="center" class="py-4">
          <v-col cols="12" md="6" class="text-center text-md-left">
            <span class="text-medium-emphasis text-body-2">
              {{ $t('nav.footer.copyright', { year: new Date().getFullYear() }) }}
            </span>
          </v-col>
          <v-col cols="12" md="6" class="text-center text-md-right">
            <v-btn
              v-for="(link, i) in footerLinks"
              :key="i"
              variant="text"
              class="text-none text-medium-emphasis text-body-2 mx-2"
              :to="link.to"
            >
              {{ $t(link.title) }}
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </v-footer>

    <!-- Global credit checkout. Opened directly from the signed-in account
         menu so buying credits never bounces the user out to the marketing
         landing's pricing section. -->
    <PayPalButton
      v-model="showCreditCheckout"
      source="user_menu"
      context="default"
    />
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useDisplay } from 'vuetify';
import LanguageSwitcher from '@/components/LanguageSwitcher.vue';
import PayPalButton from '@/components/common/PayPalButton.vue';
import { useAuthStore } from '@/stores/auth';
import { auth } from '@/firebase/config';
import { trackPricingClick } from '@/utils/analytics';
import { navigateToPricing } from '@/utils/pricingNav.mjs';

const router = useRouter();
const store = useStore();
const drawer = ref(false);
const showCreditCheckout = ref(false);
const authStore = useAuthStore();
const { mdAndDown } = useDisplay();
const isMobileNav = computed(() => mdAndDown.value);
const isLandingPage = computed(() => router.currentRoute.value.name === 'Home');
const isAnalysisRoute = computed(() => ['Analysis', 'AnalysisHub', 'Analysis360', 'AnalysisStudio'].includes(String(router.currentRoute.value.name || '')));
const currentAnalysisId = computed(() => router.currentRoute.value.params?.id || 'current');

const menuItems = [
  { title: 'nav.menu.features', section: 'how-it-works', icon: 'mdi-help-circle-outline' },
  { title: 'nav.menu.pricing', section: 'pricing', icon: 'mdi-currency-usd', onClick: handlePricingClick },
  { title: 'nav.menu.about', section: 'faq', icon: 'mdi-frequently-asked-questions' },
  { title: 'nav.menu.articles', to: '/artikel', icon: 'mdi-text-box-multiple' }
];

const mobileAppMenuItems = computed(() => [
  // Hub was removed from the analysis navigation surface (see commit
  // 7a12d29 — "remove Hub from the side menu"). The desktop sidebar
  // dropped it then; the mobile drawer needs the same treatment so
  // there is exactly one entry point per product surface (360, Studio)
  // and no third "Hub" destination that competes with them.
  isAnalysisRoute.value ? {
    title: 'analysis.productNav.report',
    icon: 'mdi-view-dashboard-outline',
    to: { name: 'Analysis360', params: { id: currentAnalysisId.value } },
  } : null,
  isAnalysisRoute.value ? {
    title: 'analysis.productNav.studio',
    icon: 'mdi-auto-fix',
    to: { name: 'AnalysisStudio', params: { id: currentAnalysisId.value } },
  } : null,
  authStore.isAuthenticated ? {
    title: 'nav.menu.analysis',
    icon: 'mdi-folder-multiple-image',
    to: { name: 'AnalysesDashboard' },
    onClick: handleMobileAnalysisClick,
  } : null,
  // The standalone "Credits kaufen" entry was removed in favor of a
  // single combined credits item further down the drawer that shows
  // the current balance AND opens the credit-package picker on tap.
  // Both intents are served by one row — no duplication.
  !authStore.isAuthenticated && !isAnalysisRoute.value ? {
    title: 'nav.menu.home',
    icon: 'mdi-home-outline',
    to: '/',
  } : null,
].filter(Boolean));

const footerLinks = [
  { title: 'nav.footer.privacy', to: '/datenschutz' },
  { title: 'nav.footer.imprint', to: '/impressum' }
];

// Brand link target. For signed-in users "/" is the marketing landing they
// have no business on — and once they're on the pricing page (Home), a
// brand click to "/" is a Home->Home navigation the guard intentionally
// ignores, leaving them stranded. Point them at their dashboard instead so
// the brand is always a working way back into the app.
const brandTarget = computed(() =>
  authStore.isAuthenticated ? { name: 'AnalysesDashboard' } : '/',
);

const showGetStartedButton = computed(() => {
  // The "Jetzt starten" CTA is aimed at first-time visitors. Authenticated
  // users have already started — they should be heading to /analysis or
  // managing credits, not seeing a "get started" call to action.
  if (authStore.isAuthenticated) return false;
  return isLandingPage.value;
});

const handleSignOut = async () => {
  drawer.value = false;
  await auth.signOut();
  store.commit('resetState');
  router.push('/');  
};

const handleMobileAnalysisClick = () => {
  // Clear any in-memory analysis state so the dashboard reflects
  // fresh data and the user doesn't see a stale concept lingering.
  // Then route to the dashboard explicitly — the v-list-item itself
  // doesn't bind `to`, the loop only fires this onClick handler.
  store.commit('resetAnalysisData');
  drawer.value = false;
  if (router.currentRoute.value.name !== 'AnalysesDashboard') {
    router.push({ name: 'AnalysesDashboard' });
  }
};

const goToSection = (section, to) => {
  if (to) {
    router.push(to);
    drawer.value = false;
    return;
  }

  if (router.currentRoute.value.path !== '/') {
    router.push('/')
    setTimeout(() => {
      const element = document.getElementById(section)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    }, 100)
  } else {
    const element = document.getElementById(section)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }
  drawer.value = false
}

const handleCreditsClick = () => {
  trackPricingClick('user_menu');
  drawer.value = false;
  // Signed-in users get the checkout modal in place. Anonymous visitors (who
  // have no account context yet) still go to the landing's pricing section —
  // navigateToPricing() carries ?pricing=1 so the authenticated Home guard
  // doesn't bounce them back to the dashboard.
  if (authStore.isAuthenticated) {
    showCreditCheckout.value = true;
    return;
  }
  navigateToPricing(router);
};

const scrollToTop = () => {
  if (router.currentRoute.value.path.includes('sign-')) {
    router.push('/');
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  drawer.value = false;
};

const handleLanguageChange = () => {
  // Force page reload
  window.location.reload();
};

const handlePricingClick = () => {
  const source = drawer.value ? 'mobile_menu' : 'nav_menu';
  trackPricingClick(source);
  goToSection('pricing');
};

const shouldHideAppBar = ref(false);

// Handler for pinning tabs: pin (fixed at top:0) if the page is scrolled more than 10px,
// and revert to its original sticky position when at the very top.
const handleScroll = () => {
  // Get current scroll position
  const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;
  
  // Only show the app bar when we're at the very top (with a small threshold)
  if (currentScrollPosition < 10) {
    shouldHideAppBar.value = false;
    return;
  }
  
  // Hide the app bar for all other scroll positions
  shouldHideAppBar.value = true;
};

onMounted(() => {
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

watch(isMobileNav, (mobileActive) => {
  if (!mobileActive) {
    drawer.value = false;
  }
});

watch(
  () => router.currentRoute.value.fullPath,
  () => {
    drawer.value = false;
  },
);
</script>

<style>
:root {
  --v-background-base: var(--background);
  --v-primary-base: var(--primary);
  --v-primary-lighten1: var(--primary-light);
  --v-primary-lighten2: var(--primary-light);
  --v-primary-lighten3: var(--primary-light);
  --v-primary-darken1: var(--primary-dark);
  --v-secondary-base: var(--text-secondary);
  --v-surface-base: var(--surface);
  --v-surface-variant-base: var(--surface-soft);
}

.v-application {
  background-color: var(--background) !important;
  overflow-y: visible !important;
}

.v-app-bar {
  background-color: var(--background) !important;
  transition: transform 0.3s ease;
}

.v-app-bar--hidden {
  transform: translateY(-100%);
}

.v-app-bar--visible {
  transform: translateY(0);
}

.klaro {
  z-index: 2000 !important;
}

.klaro .cookie-modal {
  z-index: 2001 !important;
}

.klaro .cookie-notice {
  z-index: 2001 !important;
  position: fixed !important;
}

/* Add these new styles after the existing ones */

/* For Webkit browsers (Chrome, Safari) */
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
}

::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* For Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) rgba(0, 0, 0, 0.1);
}

/* Keep normal document scrolling so sticky landing sections can pin correctly. */
.v-main {
  overflow-y: visible !important;
  padding-top: var(--v-app-bar-height, 64px) !important;
}

/* Let scrolling happen on html/body. Any `overflow` other than `visible`
   on .v-application creates a sticky containing block that prevents
   `.analysis-tabs` (position: sticky) from sticking below the app bar —
   the tab bar would scroll out of view on long pages. Both axes need to
   be visible: overflow-x: hidden alone is enough to establish the
   containing block. (.v-main is already set above with padding-top for
   the sticky landing sections.) */
.v-application {
  overflow: visible !important;
}

/* Klaro Modal Styling */
.klaro .cookie-modal {
  z-index: 2001 !important;
}

.klaro .cookie-modal .cm-modal {
  left: 50% !important;
  transform: translate(-50%, -50%) !important;
  max-height: 98vh !important;
  top: 50% !important;
  position: fixed !important;
  border-radius: 8px !important;
  width: 90% !important;
  max-width: 640px !important;
  background: white !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1) !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) {
  position: fixed !important;
  left: 16px !important;
  right: 16px !important;
  bottom: 16px !important;
  top: auto !important;
  width: auto !important;
  max-width: 420px !important;
  margin-left: auto !important;
  border-radius: 18px !important;
  background: rgba(255, 255, 255, 0.98) !important;
  color: var(--text-primary) !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.18) !important;
  backdrop-filter: blur(12px) !important;
  z-index: 2001 !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body {
  padding: 1rem 1rem 1.1rem !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body p {
  color: var(--text-secondary) !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-ok {
  display: flex !important;
  flex-direction: column !important;
  gap: 0.75rem !important;
  align-items: stretch !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-buttons {
  display: flex !important;
  flex-direction: column !important;
  gap: 0.75rem !important;
  margin-top: 0 !important;
  width: 100% !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-buttons .cm-btn,
.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-ok .cm-btn {
  width: 100% !important;
  margin-top: 0 !important;
  border-radius: 12px !important;
}

.klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-learn-more {
  margin-top: 0 !important;
}

.klaro .cookie-modal .cm-modal .cm-header {
  padding: 1em !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.klaro .cookie-modal .cm-modal .cm-body {
  padding: 1em !important;
}

.klaro .cookie-modal .cm-modal .cm-footer {
  padding: 1em !important;
  border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
}

.klaro .cookie-modal .cm-modal .cm-footer .cm-buttons {
  display: flex !important;
  gap: 8px !important;
}

.klaro .cookie-modal .cm-modal .cm-footer .cm-btn {
  padding: 8px 16px !important;
  border-radius: 4px !important;
  font-weight: var(--font-weight-semibold) !important;
}

/* Add overlay styling */
.klaro .cookie-modal .cm-bg {
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(2px) !important;
  position: fixed !important;
  left: 0 !important;
  right: 0 !important;
  top: 0 !important;
  bottom: 0 !important;
}

/* Prevent scrolling when modal is open */
body.cm-modal-open {
  overflow: hidden !important;
}

/* Make sure modal content is scrollable if needed */
.klaro .cookie-modal .cm-modal .cm-body {
  max-height: calc(98vh - 200px) !important;
  overflow-y: auto !important;
}

/* Style the service items */
.klaro .cookie-modal .cm-services {
  gap: 8px !important;
}

.klaro .cookie-modal .cm-service {
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  border-radius: 4px !important;
  padding: 1em !important;
  background: rgba(0, 0, 0, 0.02) !important;
}

/* Improve button styling */
.klaro .cm-btn {
  transition: all 0.2s ease !important;
}

.klaro .cm-btn:hover {
  opacity: 0.9 !important;
  transform: translateY(-1px) !important;
}

.klaro .cm-btn.cm-btn-success {
  background: var(--primary) !important;
}

.klaro .cm-btn.cm-btn-decline {
  background: #dc3545 !important;
}

/* Add responsive styles for the app bar */
@media (max-width: 600px) {
  .v-app-bar .v-toolbar__content {
    padding: 4px 0 !important;
  }

  .v-app-bar .v-icon {
    font-size: 24px !important;
  }

  .v-app-bar .v-container {
    padding-top: 4px !important;
    padding-bottom: 0px !important;
  }

  .klaro .cookie-notice:not(.cookie-modal-notice) {
    left: 12px !important;
    right: 12px !important;
    bottom: 12px !important;
    max-width: none !important;
    border-radius: 14px !important;
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14) !important;
  }

  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body {
    padding: 0.65rem 0.75rem 0.75rem !important;
  }

  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body p {
    font-size: 0.82rem !important;
    line-height: 1.35 !important;
    margin-bottom: 0.25rem !important;
  }

  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-ok,
  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-buttons {
    gap: 0.5rem !important;
  }

  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-buttons .cm-btn,
  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-ok .cm-btn {
    min-height: 40px !important;
    padding: 0.55rem 0.75rem !important;
  }

  .klaro .cookie-notice:not(.cookie-modal-notice) .cn-body .cn-learn-more {
    font-size: 0.9rem !important;
  }
}

/* App bar transition styles */
.app-bar-transition {
  transition: transform 0.3s ease-in-out !important;
}

.app-bar-hidden {
  transform: translateY(-100%) !important;
}

.app-bar-visible {
  transform: translateY(0) !important;
}

/* Update main content padding */
.v-main {
  padding-top: 64px !important;
}

@media (min-width: 960px) {
  .v-main {
    padding-top: 80px !important;
  }
}

/* Mobile nav drawer header — close affordance and balanced spacing.
   Without this the drawer relied on outside-tap to dismiss, which isn't
   discoverable on touch. */
.mobile-nav-drawer__header {
  display: flex;
  justify-content: flex-end;
  padding: 8px 8px 0;
}

.mobile-nav-drawer {
  background: #fffaf5 !important;
  border-left: 1px solid rgba(109, 59, 31, 0.16) !important;
  box-shadow: -24px 0 60px rgba(42, 28, 18, 0.22) !important;
}

.mobile-nav-drawer .v-list {
  background: transparent !important;
  padding: 8px 12px 18px !important;
}

.v-overlay__scrim {
  background: rgba(24, 17, 12, 0.32) !important;
}
</style>
